import React, { useEffect, useRef, useState, useCallback } from 'react';
import './Students.css';
import Layout from '../../../../components/Layout';
import {
  Autocomplete,
  Box,
  Input,
  InputAdornment,
  TextField,
  Typography,
  Tabs,
  Tab,
  Button,
  FormControl,
  Select,
  MenuItem,
  Popover,
  Paper,
  Popper,
  Slider,
} from '@mui/material';
import AdminNavbar from '../../../../components/Admin/AdminNavbar/AdminNavbar';
import SearchIcon from '@mui/icons-material/Search';
import FilterIcon from '../../../../assets/Admin/RequestImages/filter-icon.svg';
import ColoredFilterIcon from '../../../../assets/Admin/RequestImages/colored-filter-icon.svg';
import { axiosInstance } from '../../../../utils/tokenUtils';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import StudentTable from '../../../../components/Admin/UserManagement/Student/StudentTable/StudentTable';
import PropTypes from 'prop-types';
import usePagination from '@mui/material/usePagination';
import { styled } from '@mui/material/styles';
import { TableSkeleton } from '../../../../components/Admin/SkeletonLoader/SkeletonLoader';

const SchoolLevelArray = [
  { value: 'All', label: 'All' },
  { value: 'elementary', label: 'Elementary' },
  { value: 'high_school', label: 'High School' },
  { value: 'middle_school', label: 'Middle School' },
  { value: 'university', label: 'University' },
];

const GenderArray = [
  { value: 'All', label: 'All' },
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
];

function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2, pb: 0 }}>{children}</Box>}
    </div>
  );
}

CustomTabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

// Debounce hook
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

const List = styled('ul')({
  listStyle: 'none',
  padding: 0,
  margin: '6px 0',
  display: 'flex',
  justifyContent: 'center',
});

const Students = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [schoolLevel, setSchoolLevel] = useState('All');
  const [country, setCountry] = useState('All');
  const [city, setCity] = useState('All');
  const [gender, setGender] = useState('All');
  const [totalSpent, setTotalSpent] = useState([0, 10000]);

  // Temporary filter states
  const [tempSchoolLevel, setTempSchoolLevel] = useState('All');
  const [tempCountry, setTempCountry] = useState('All');
  const [tempCity, setTempCity] = useState('All');
  const [tempGender, setTempGender] = useState('All');
  const [tempTotalSpent, setTempTotalSpent] = useState([0, 10000]);
  const [tempSearchTerm, setTempSearchTerm] = useState('');

  // Pagination states
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Bulk selection states
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Data states
  const [studentData, setStudentData] = useState([]);
  const [enableStudentData, setEnableStudentData] = useState([]);
  const [disableStudentData, setDisableStudentData] = useState([]);

  // UI states
  const [openFilter, setOpenFilter] = useState(false);
  const [value, setValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');

  // Dropdown data
  const [countryList, setCountryList] = useState([]);
  const [cityList, setCityList] = useState([]);

  // Search states for dropdowns
  const [countrySearch, setCountrySearch] = useState('');
  const [citySearch, setCitySearch] = useState('');

  const [anchorEl, setAnchorEl] = useState(null);

  // Debounced search values
  const debouncedCountrySearch = useDebounce(countrySearch, 300);
  const debouncedCitySearch = useDebounce(citySearch, 300);
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  // Create refs to access current values without triggering re-renders
  const countryListRef = useRef([]);
  const cityListRef = useRef([]);

  // Update refs when state changes
  useEffect(() => {
    countryListRef.current = countryList;
  }, [countryList]);
  useEffect(() => {
    cityListRef.current = cityList;
  }, [cityList]);

  const handleChange = (event, newValue) => {
    setValue(newValue);
    // Reset to first page when changing tabs
    setPage(1);
  };

  // API Functions
  const fetchCountries = async (searchQuery = '') => {
    try {
      const url = searchQuery ? `/auth/countries?search_query=${searchQuery}` : '/auth/countries';
      const { data } = await axiosInstance.get(url);
      setCountryList(data || []);
    } catch (error) {
      console.log('Error fetching countries:', error);
      setCountryList([]);
    }
  };

  const fetchCities = async (countryId, searchQuery = '') => {
    try {
      const url = searchQuery
        ? `/auth/countries/${countryId}/cities?search_query=${searchQuery}`
        : `/auth/countries/${countryId}/cities`;
      const { data } = await axiosInstance.get(url);
      setCityList(data || []);
    } catch (error) {
      console.log('Error fetching cities:', error);
      setCityList([]);
    }
  };

  const fetchStudentData = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();

      // Add pagination parameters
      params.append('page', page.toString());
      params.append('page_size', pageSize.toString());

      if (debouncedSearchTerm) params.append('query', debouncedSearchTerm);
      if (schoolLevel !== 'All') params.append('school_level', schoolLevel.toLowerCase());
      if (country !== 'All') {
        const countryObj = countryListRef.current.find(c => c.name === country);
        if (countryObj) params.append('country', countryObj.name);
      }
      if (city !== 'All') {
        const cityObj = cityListRef.current.find(c => c.name === city);
        if (cityObj) params.append('city', cityObj.name);
      }
      if (gender !== 'All') params.append('gender', gender);
      // if (totalSpent[0] > 0 || totalSpent[1] < 10000) {
      //   params.append('min_spent', `${totalSpent[0]}`);
      //   params.append('max_spent', `${totalSpent[1]}`);
      // }

      if (sortBy) {
        const sortParam = `${sortOrder === 'desc' ? '-' : ''}${sortBy}`;
        params.append('sort_by', sortParam);
      }

      // Add active/inactive filter based on current tab
      if (value === 1) {
        params.append('is_active', 'true');
      } else if (value === 2) {
        params.append('is_active', 'false');
      }

      // Fetch data for current tab only
      const response = await axiosInstance.get(`/admin/students?${params.toString()}`);
      const data = response.data?.results || [];
      const count = response.data?.count || 0;

      // Update the appropriate data state based on current tab
      switch (value) {
        case 0: // All
          setStudentData(data);
          setTotalCount(count);
          break;
        case 1: // Enable
          setEnableStudentData(data);
          setTotalCount(count);
          break;
        case 2: // Disable
          setDisableStudentData(data);
          setTotalCount(count);
          break;
        default:
          setStudentData(data);
          setTotalCount(count);
      }
    } catch (error) {
      console.log('Error fetching student data:', error);
    } finally {
      setLoading(false);
    }
  }, [
    debouncedSearchTerm,
    schoolLevel,
    country,
    city,
    gender,
    totalSpent,
    page,
    pageSize,
    value,
    sortBy,
    sortOrder,
  ]);

  // Initial data fetch
  useEffect(() => {
    let isMounted = true;

    const initializeData = async () => {
      setLoading(true);
      try {
        await Promise.all([fetchCountries()]);

        if (isMounted) {
          // Initial student data fetch for default tab (All)
          try {
            const sortQuery = sortBy ? `&sort_by=${sortOrder === 'desc' ? '-' : ''}${sortBy}` : '';
            const allStudentsResponse = await axiosInstance.get(
              `/admin/students?page=1&page_size=${pageSize}${sortQuery}`
            );
            setStudentData(allStudentsResponse.data?.results || []);
            setTotalCount(allStudentsResponse.data?.count || 0);
          } catch (error) {
            console.log('Error fetching initial student data:', error);
          }
        }
      } catch (error) {
        console.log('Error initializing data:', error);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    initializeData();

    return () => {
      isMounted = false;
    };
  }, [pageSize]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearchTerm]);

  useEffect(() => {
    fetchStudentData();
  }, [page, pageSize, fetchStudentData]);

  // Fetch countries when search changes
  useEffect(() => {
    if (debouncedCountrySearch !== countrySearch) return;
    fetchCountries(debouncedCountrySearch);
  }, [debouncedCountrySearch]);

  // Fetch cities when country or city search changes
  useEffect(() => {
    // When in filter modal, use tempCountry
    if (openFilter && tempCountry !== 'All') {
      const countryObj = countryList.find(c => c.name === tempCountry);
      if (countryObj) {
        fetchCities(countryObj.id, debouncedCitySearch);
      }
    }
    // When not in filter modal or after applying filters
    else if (!openFilter && country !== 'All') {
      const countryObj = countryList.find(c => c.name === country);
      if (countryObj) {
        fetchCities(countryObj.id, debouncedCitySearch);
      }
    } else {
      setCityList([]);
      if (openFilter) {
        setTempCity('All');
      } else {
        setCity('All');
      }
    }
  }, [country, tempCountry, debouncedCitySearch, countryList, openFilter]);

  // Update tempCity when country changes in filter modal to avoid stale city selections
  useEffect(() => {
    if (openFilter && tempCountry === 'All') {
      setTempCity('All');
    }
  }, [tempCountry, openFilter]);

  // Event Handlers
  const handleClickToOpenFilter = event => {
    setAnchorEl(event.currentTarget);
    setOpenFilter(true);

    // Initialize temp filters with current values
    setTempSchoolLevel(schoolLevel);
    setTempCountry(country);
    setTempCity(city);
    setTempGender(gender);
    setTempTotalSpent(totalSpent);
  };

  const handleApplyFilter = () => {
    setOpenFilter(false);
    setAnchorEl(null);

    // Apply filters from temp state to actual state
    setSchoolLevel(tempSchoolLevel);
    setCountry(tempCountry);
    setCity(tempCity);
    setGender(tempGender);
    setTotalSpent(tempTotalSpent);

    // Reset to first page
    setPage(1);

    // Fetch data with new filters
    fetchStudentData();
  };

  const handleCloseFilter = () => {
    setAnchorEl(null);
    setOpenFilter(false);

    // Reset temp filters to current values (discard changes)
    setTempSchoolLevel(schoolLevel);
    setTempCountry(country);
    setTempCity(city);
    setTempGender(gender);
    setTempTotalSpent(totalSpent);
  };

  // Bulk selection handlers
  const toggleSelectionMode = () => {
    setSelectionMode(prev => !prev);
    if (selectionMode) {
      // Leaving selection mode – clear selections
      setSelectedIds(new Set());
    }
  };

  // Handlers passed to table components
  const handleSelectOne = (id, checked) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (checked) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  };

  const handleSelectAllVisible = (ids, checked) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      ids.forEach(id => {
        if (checked) {
          next.add(id);
        } else {
          next.delete(id);
        }
      });
      return next;
    });
  };

  const handleClearAll = () => {
    // Update only temporary states
    setTempSchoolLevel('All');
    setTempCountry('All');
    setTempCity('All');
    setTempGender('All');
    setTempTotalSpent([0, 10000]);
    setCountrySearch('');
    setCitySearch('');
  };

  const handleChangeSearch = e => {
    setSearchTerm(e.target.value);
    setTempSearchTerm(e.target.value);
  };

  // Total spent slider handlers
  const handleTotalSpentChange = (event, newValue) => {
    setTempTotalSpent(newValue);
  };

  const valuetext = value => {
    return `$${value}`;
  };

  // Pagination handlers
  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handlePageSizeChange = event => {
    setPageSize(event.target.value);
    setPage(1); // Reset to first page when changing page size
  };

  const handleSortByChange = event => {
    setSortBy(event.target.value);
    setPage(1);
  };

  const handleSortOrderChange = event => {
    setSortOrder(event.target.value);
    setPage(1);
  };

  // Helper function for pagination text formatting
  const formatText = input => {
    return input
      .replace(/_/g, ' ') // Replace underscores with spaces
      .replace(/\b\w/g, char => char.toUpperCase()); // Capitalize first letter of each word
  };

  // Get current data based on active tab
  const getCurrentData = () => {
    switch (value) {
      case 0: // All
        return studentData;
      case 1: // Enable
        return enableStudentData;
      case 2: // Disable
        return disableStudentData;
      default:
        return studentData;
    }
  };

  // Pagination setup
  const { items } = usePagination({
    count: Math.ceil(totalCount / pageSize),
    page: page,
    onChange: handlePageChange,
  });

  const currentData = getCurrentData();

  return (
    <Layout title="Klass Online | Learners">
      <Box className="relative h-screen w-full overflow-hidden">
        <Box className="sticky top-0 left-0 right-0 z-[1000] bg-white pr-[10px]">
          <AdminNavbar />
        </Box>
        <Box className="h-[calc(100vh-64px)] overflow-auto relative bg-[#F9F9F9] rounded-lg mr-[10px]">
          <Box className="flex flex-col p-[1rem] gap-4">
            <Box className="flex justify-between items-center">
              <Typography className="teacher-title">Learners</Typography>
              <Box className="flex items-center gap-4 w-[40%]">
                <Input
                  sx={{
                    textDecoration: 'none',
                    border: '1px solid var(--Stroke, #D5DEE3)',
                    borderRadius: '56px',
                    '&:before, &:after': {
                      borderBottom: 'none !important',
                    },
                    '&:hover:not(.Mui-disabled):before': {
                      borderBottom: 'none !important',
                    },
                    padding: '8px 10px',
                    '& .MuiInputBase-input': {
                      padding: '3px 5px 3px 5px',
                    },
                    background: '#FFF',
                    width: '100%',
                    height: '40px',
                  }}
                  type="search"
                  placeholder="Search"
                  aria-label="Search"
                  value={searchTerm}
                  onChange={handleChangeSearch}
                  startAdornment={<SearchIcon className="search-icon-css" />}
                />
                {/* Removed Select button for always-on selection mode */}
                {openFilter ? (
                  <img
                    src={ColoredFilterIcon}
                    alt="Filter-icon"
                    style={{ width: '25px', height: '25px', cursor: 'pointer' }}
                    onClick={handleClickToOpenFilter}
                  />
                ) : (
                  <img
                    src={FilterIcon}
                    alt="Filter-icon"
                    style={{ width: '25px', height: '25px', cursor: 'pointer' }}
                    onClick={handleClickToOpenFilter}
                  />
                )}
              </Box>
            </Box>

            {/* Filter Modal */}
            <Popover
              open={openFilter}
              anchorEl={anchorEl}
              onClose={handleCloseFilter}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              PaperProps={{
                className: 'filter-modal-container',
              }}
            >
              {/* Header */}
              <Box className="filter-modal-header">
                <Typography className="filter-modal-title">Filters</Typography>
              </Box>

              {/* Scrollable Content */}
              <Box className="filter-modal-content">
                {/* Grid for filter items */}
                <Box className="filter-grid">
                  {/* School Level Filter */}
                  <Box className="filter-item">
                    <label className="filter-label">School Level</label>
                    <FormControl sx={{ width: '100%' }}>
                      <Select
                        value={tempSchoolLevel}
                        onChange={e => setTempSchoolLevel(e.target.value)}
                        displayEmpty
                        className="filter-input"
                        IconComponent={KeyboardArrowDownIcon}
                        MenuProps={{
                          PaperProps: {
                            style: {
                              zIndex: 1500,
                            },
                          },
                        }}
                        sx={{
                          '& .MuiOutlinedInput-notchedOutline': {
                            border: 'none',
                          },
                        }}
                      >
                        {SchoolLevelArray.map(level => (
                          <MenuItem
                            key={level.value}
                            value={level.value}
                            className="dropdown-option"
                          >
                            {level.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>

                  {/* Country Filter with Search */}
                  <Box className="filter-item">
                    <label className="filter-label">Country</label>
                    <Autocomplete
                      value={
                        tempCountry === 'All'
                          ? null
                          : countryList.find(c => c.name === tempCountry) || null
                      }
                      onChange={(event, newValue) => {
                        setTempCountry(newValue ? newValue.name : 'All');
                      }}
                      inputValue={countrySearch}
                      onInputChange={(event, newInputValue) => {
                        setCountrySearch(newInputValue);
                      }}
                      options={countryList}
                      getOptionLabel={option => option.name || ''}
                      PopperComponent={props => (
                        <Popper
                          {...props}
                          style={{
                            ...props.style,
                            zIndex: 1500,
                            width: props.anchorEl ? props.anchorEl.offsetWidth : 'auto',
                          }}
                          modifiers={[
                            {
                              name: 'flip',
                              enabled: false,
                            },
                            {
                              name: 'preventOverflow',
                              enabled: false,
                            },
                          ]}
                        />
                      )}
                      ListboxProps={{
                        style: {
                          maxHeight: 200,
                        },
                      }}
                      renderOption={(props, option) => (
                        <li {...props} className="dropdown-option">
                          {option.name}
                        </li>
                      )}
                      renderInput={params => (
                        <TextField
                          {...params}
                          placeholder="Search countries..."
                          InputProps={{
                            ...params.InputProps,
                            className: 'filter-input',
                            endAdornment: (
                              <InputAdornment position="end">
                                <KeyboardArrowDownIcon sx={{ color: '#949DA6' }} />
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              '& fieldset': {
                                border: 'none',
                              },
                            },
                          }}
                        />
                      )}
                    />
                  </Box>

                  {/* City Filter with Search - Only show when country is selected */}
                  {tempCountry !== 'All' && (
                    <Box className="filter-item">
                      <label className="filter-label">City</label>
                      <Autocomplete
                        value={
                          tempCity === 'All'
                            ? null
                            : cityList.find(c => c.name === tempCity) || null
                        }
                        onChange={(event, newValue) => {
                          setTempCity(newValue ? newValue.name : 'All');
                        }}
                        inputValue={citySearch}
                        onInputChange={(event, newInputValue) => {
                          setCitySearch(newInputValue);
                        }}
                        options={cityList}
                        getOptionLabel={option => option.name || ''}
                        PopperComponent={props => (
                          <Popper
                            {...props}
                            style={{
                              ...props.style,
                              zIndex: 1500,
                              width: props.anchorEl ? props.anchorEl.offsetWidth : 'auto',
                            }}
                            modifiers={[
                              {
                                name: 'flip',
                                enabled: false,
                              },
                              {
                                name: 'preventOverflow',
                                enabled: false,
                              },
                            ]}
                          />
                        )}
                        ListboxProps={{
                          style: {
                            maxHeight: 200,
                          },
                        }}
                        renderOption={(props, option) => (
                          <li {...props} className="dropdown-option">
                            {option.name}
                          </li>
                        )}
                        renderInput={params => (
                          <TextField
                            {...params}
                            placeholder="Search cities..."
                            InputProps={{
                              ...params.InputProps,
                              className: 'filter-input',
                              endAdornment: (
                                <InputAdornment position="end">
                                  <KeyboardArrowDownIcon sx={{ color: '#949DA6' }} />
                                </InputAdornment>
                              ),
                            }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                '& fieldset': {
                                  border: 'none',
                                },
                              },
                            }}
                          />
                        )}
                      />
                    </Box>
                  )}

                  {/* Gender Filter */}
                  <Box className="filter-item">
                    <label className="filter-label">Gender</label>
                    <FormControl sx={{ width: '100%' }}>
                      <Select
                        value={tempGender}
                        onChange={e => setTempGender(e.target.value)}
                        displayEmpty
                        className="filter-input"
                        IconComponent={KeyboardArrowDownIcon}
                        MenuProps={{
                          PaperProps: {
                            style: {
                              zIndex: 1500,
                            },
                          },
                        }}
                        sx={{
                          '& .MuiOutlinedInput-notchedOutline': {
                            border: 'none',
                          },
                        }}
                      >
                        {GenderArray.map(item => (
                          <MenuItem key={item.value} value={item.value} className="dropdown-option">
                            {item.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                </Box>

                {/* Total Spent Slider - Outside grid, full width */}
                {/* <Box className="filter-revenue-container">
                  <label className="filter-label">Total Spent Range</label>
                  <Box className="filter-revenue-wrapper">
                    <Slider
                      value={tempTotalSpent}
                      onChange={handleTotalSpentChange}
                      valueLabelDisplay="off"
                      valueLabelFormat={valuetext}
                      getAriaValueText={valuetext}
                      min={0}
                      max={10000}
                      step={10}
                      sx={{
                        color: '#0A5EF3',
                        height: 8,
                        '& .MuiSlider-thumb': {
                          height: 24,
                          width: 24,
                          backgroundColor: '#fff',
                          border: '2px solid #0A5EF3',
                          '&:focus, &:hover, &.Mui-active, &.Mui-focusVisible': {
                            boxShadow: '0 0 0 8px rgba(10, 94, 243, 0.16)',
                          },
                        },
                        '& .MuiSlider-valueLabel': {
                          backgroundColor: '#0A5EF3',
                          fontFamily: 'Poppins',
                          fontSize: '12px',
                          fontWeight: 500,
                        },
                        '& .MuiSlider-rail': {
                          height: 8,
                          borderRadius: 4,
                          backgroundColor: '#E5E7EB',
                        },
                        '& .MuiSlider-track': {
                          height: 8,
                          borderRadius: 4,
                        },
                      }}
                    />
                    <Box className="filter-revenue-values">
                      <Typography className="filter-revenue-value">$ {tempTotalSpent[0]}</Typography>
                      <Typography className="filter-revenue-value">$ {tempTotalSpent[1]}</Typography>
                    </Box>
                  </Box>
                </Box> */}
              </Box>

              {/* Buttons (Sticky Footer) */}
              <Box className="filter-buttons-container">
                <Button variant="outlined" onClick={handleClearAll} className="filter-clear-button">
                  Clear All
                </Button>
                <Button
                  variant="contained"
                  onClick={handleApplyFilter}
                  className="filter-apply-button"
                >
                  Apply
                </Button>
              </Box>
            </Popover>

            <Box className="teacher-table-container">
              <Box sx={{ width: '100%' }}>
                <Box
                  sx={{
                    borderBottom: 1,
                    borderColor: 'divider',
                    background: '#FFF',
                  }}
                >
                  <Tabs value={value} onChange={handleChange} aria-label="basic tabs example">
                    <Tab
                      label="All"
                      aria-label="all tab"
                      sx={{ textTransform: 'none' }}
                      {...a11yProps(0)}
                    />
                    <Tab
                      label="Enable"
                      aria-label="pending tab"
                      sx={{ textTransform: 'none' }}
                      {...a11yProps(1)}
                    />
                    <Tab label="Disable" sx={{ textTransform: 'none' }} {...a11yProps(2)} />
                  </Tabs>
                </Box>

                {/* Sort By */}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    gap: '8px',
                    // background: '#FFF',
                    px: 2,
                    py: 1,
                    // borderBottom: '1px solid #E5E7EB',
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: 'Poppins',
                      fontSize: '14px',
                      color: '#14171F',
                    }}
                  >
                    Sort by
                  </Typography>
                  <Select
                    value={sortBy}
                    onChange={handleSortByChange}
                    displayEmpty
                    sx={{
                      height: '30px',
                      minWidth: '160px',
                      fontFamily: 'Poppins',
                      fontSize: '14px',
                      '& .MuiSelect-select': {
                        padding: '6px 8px 4px 8px',
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        border: '1px solid #D5DEE3',
                        borderRadius: '8px',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        border: '1px solid #D5DEE3',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        border: '1px solid #D5DEE3',
                      },
                      background: '#FFF',
                    }}
                    renderValue={selected => {
                      if (!selected) {
                        return <span style={{ color: '#949DA6' }}>Select</span>;
                      }
                      if (selected === 'total_spent') return 'Total spent';
                      if (selected === 'enrolled_on') return 'Enrolled on';
                      if (selected === 'total_sessions') return 'Total sessions';
                      return selected;
                    }}
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    <MenuItem value="total_spent">Total spent</MenuItem>
                    <MenuItem value="enrolled_on">Enrolled on</MenuItem>
                    <MenuItem value="total_sessions">Total sessions</MenuItem>
                  </Select>

                  <Select
                    value={sortOrder}
                    onChange={handleSortOrderChange}
                    displayEmpty
                    disabled={!sortBy}
                    sx={{
                      height: '30px',
                      minWidth: '120px',
                      fontFamily: 'Poppins',
                      fontSize: '14px',
                      '& .MuiSelect-select': {
                        padding: '6px 8px 4px 8px',
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        border: '1px solid #D5DEE3',
                        borderRadius: '8px',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        border: '1px solid #D5DEE3',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        border: '1px solid #D5DEE3',
                      },
                      background: '#FFF',
                    }}
                    renderValue={selected => {
                      if (!selected) {
                        return <span style={{ color: '#949DA6' }}>Order</span>;
                      }
                      return selected === 'desc' ? 'Desc' : 'Asc';
                    }}
                  >
                    <MenuItem value="asc">Asc</MenuItem>
                    <MenuItem value="desc">Desc</MenuItem>
                  </Select>
                </Box>

                {loading ? (
                  <TableSkeleton />
                ) : (
                  <>
                    <CustomTabPanel value={value} index={0}>
                      <StudentTable
                        searchTerm={searchTerm}
                        studentData={currentData}
                        category={schoolLevel}
                        country={country}
                        city={city}
                        gender={gender}
                        totalSpent={totalSpent}
                        value={value}
                        selectionMode={true}
                        selectedIds={selectedIds}
                        onSelectOne={handleSelectOne}
                        onSelectAll={handleSelectAllVisible}
                      />
                    </CustomTabPanel>
                    <CustomTabPanel value={value} index={1}>
                      <StudentTable
                        searchTerm={searchTerm}
                        studentData={currentData}
                        category={schoolLevel}
                        country={country}
                        city={city}
                        gender={gender}
                        totalSpent={totalSpent}
                        value={value}
                        selectionMode={true}
                        selectedIds={selectedIds}
                        onSelectOne={handleSelectOne}
                        onSelectAll={handleSelectAllVisible}
                      />
                    </CustomTabPanel>
                    <CustomTabPanel value={value} index={2}>
                      <StudentTable
                        searchTerm={searchTerm}
                        studentData={currentData}
                        category={schoolLevel}
                        country={country}
                        city={city}
                        gender={gender}
                        totalSpent={totalSpent}
                        value={value}
                        selectionMode={true}
                        selectedIds={selectedIds}
                        onSelectOne={handleSelectOne}
                        onSelectAll={handleSelectAllVisible}
                      />
                    </CustomTabPanel>
                  </>
                )}
              </Box>
            </Box>

            {/* Pagination UI */}
            {!loading && currentData.length > 0 && (
              <Box className="student-pagination-container">
                <Box className="student-pagination-info">
                  <Typography className="student-pagination-text">Showing</Typography>
                  <Select
                    value={pageSize}
                    onChange={handlePageSizeChange}
                    className="student-pagination-select"
                    sx={{
                      height: '30px',
                      minWidth: '20px',
                      '& .MuiSelect-select': {
                        padding: '6px 4px 4px 8px',
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        border: 'none',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        border: 'none',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        border: 'none',
                      },
                    }}
                  >
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={15}>15</MenuItem>
                    <MenuItem value={20}>20</MenuItem>
                    <MenuItem value={30}>30</MenuItem>
                  </Select>
                  <Typography className="student-pagination-text">
                    of {totalCount} learners
                  </Typography>
                </Box>
                <nav>
                  <List>
                    {items.map(({ page: itemPage, type, selected, ...item }, index) => {
                      let children = null;

                      if (type === 'start-ellipsis' || type === 'end-ellipsis') {
                        children = <span className="student-pagination-ellipsis">…</span>;
                      } else if (type === 'page') {
                        children = (
                          <button
                            type="button"
                            className={`student-pagination-button ${selected ? 'selected' : ''}`}
                            {...item}
                          >
                            {itemPage}
                          </button>
                        );
                      } else {
                        children = (
                          <button type="button" className="student-pagination-button" {...item}>
                            {formatText(type)}
                          </button>
                        );
                      }

                      return <li key={index}>{children}</li>;
                    })}
                  </List>
                </nav>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Layout>
  );
};

export default Students;
